/**
 * §17 — starting an entry the bank never reported.
 *
 * WHY THIS IS IN THE TOP BAR
 *
 * It was a link inside the queue that opened a four-field form. The operator
 * who commissioned this application asked where invoices were supposed to be
 * entered, which settles the question: if the person who specified it cannot
 * find it, it is not findable.
 *
 * It is a BUTTON and not another nav link, because it is an action rather than
 * a destination. Everything else in that bar is a place.
 *
 * WHAT THE MENU PICKS IS A FORM, NOT A STORED TYPE
 *
 * There is no `entry_type` column and there should not be one. What makes an
 * entry an invoice is already in the data -- a debit to the receivables
 * control account, a party who is a customer, an invoice number -- and the
 * matching screen finds receivables by the account's ROLE, never by a flag.
 * A stored type would be a second answer to "what is this", and the two would
 * eventually disagree. That is the same trap as keeping a copy of the party
 * name in `counterparty`, one layer up.
 *
 * So the choice travels in the URL as `?kind=`, decides which fields the
 * editor shows and what it prefills, and is forgotten the moment the entry is
 * saved.
 *
 * NO BILL YET. §6.7 covers accounts payable and the chart carries the payable
 * control role, but there are no bills to shape the form against. The menu is
 * the cheap part; adding the third item when there is a real one to look at is
 * better than guessing at it now.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepsNetAssets, useBusiness, useKeepsGoods } from '../auth/AuthProvider.js';

export function NewEntryMenu() {
  const navigate = useNavigate();
  const nonprofit = keepsNetAssets(useBusiness());
  // 0086: the goods entries, for a business that keeps goods.
  const keepsGoods = useKeepsGoods();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  /*
   * Close on a click anywhere else, and on Escape.
   *
   * `mousedown` rather than `click`: a menu that closes on click swallows the
   * first click outside it, so dismissing the menu and pressing the thing you
   * were aiming at take two clicks.
   */
  useEffect(() => {
    if (!open) return;

    function onPointer(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function start(kind: 'journal' | 'invoice' | 'pledge' | 'receive' | 'giveaway' | 'shrinkage' | 'transfer') {
    setOpen(false);
    // `new` rather than an id. Nothing is written until the entry is saved, so
    // abandoning one costs nothing -- the old form's Cancel did the same job
    // by never creating the row in the first place.
    navigate(`/journals/new?kind=${kind}`);
  }

  return (
    <div className="new-entry" ref={root}>
      <button
        type="button"
        className="new-entry-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        New Entry ▾
      </button>
      {open && (
        <div className="new-entry-menu" role="menu">
          <button type="button" role="menuitem" onClick={() => start('journal')}>
            Journal Entry
          </button>
          <button type="button" role="menuitem" onClick={() => start('invoice')}>
            Invoice
          </button>
          {/* 0084 (§B.17.4): a donor's promise, on Pledges Receivable; a non-profit's. */}
          {nonprofit && (
            <button type="button" role="menuitem" onClick={() => start('pledge')}>
              Pledge
            </button>
          )}
          {/*
            0086 (§14.7): goods in and out. Each opens a journal entry with its
            lines laid out; a sale is an invoice with Add Item.
          */}
          {keepsGoods && (
            <>
              <button type="button" role="menuitem" onClick={() => start('receive')}>
                Receive Goods
              </button>
              <button type="button" role="menuitem" onClick={() => start('giveaway')}>
                Give Goods Away
              </button>
              <button type="button" role="menuitem" onClick={() => start('shrinkage')}>
                Record Shrinkage
              </button>
              {/* 0088 (§15.3): goods from one pool to another, a stock account, a fund or a project. */}
              <button type="button" role="menuitem" onClick={() => start('transfer')}>
                Move Stock
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
